import { useState, useEffect } from "react";

// ─── Palette tokens ───────────────────────────────────────────────────────────
const C = {
    bg: "#f8fafc",
    bgSubtle: "#f1f5f9",
    white: "#ffffff",
    navBg: "#fefce8",
    navBorder: "#fde68a",
    blue: "#1e3a8a",
    blueLight: "#2563eb",
    blueDark: "#172554",
    amber: "#d97706",
    amberLight: "#fbbf24",
    amberPale: "#fef3c7",
    sky: "#0ea5e9",
    textHi: "#0f172a",
    textMid: "#475569",
    textLow: "#94a3b8",
    border: "#e2e8f0",
    glassBg: "rgba(255,255,255,0.65)",
    glassBorder: "rgba(255,255,255,0.5)",
};

// ─── Global styles ────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .br-root {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #f8fafc;
    color: #0f172a;
    min-height: 100vh;
    overflow-x: hidden;
    font-size: 16px;
    line-height: 1.5;
  }

  .br-root h1, .br-root h2, .br-root h3, .br-root h4, .br-root h5, .br-root h6 {
    font-family: 'Outfit', sans-serif;
  }

  .br-root h1 { font-size: 48px; font-weight: 700; line-height: 1.25; }
  @media (min-width: 768px) { .br-root h1 { font-size: 72px; } }

  .br-root h2 { font-size: 36px; font-weight: 700; line-height: 1.25; }
  @media (min-width: 768px) { .br-root h2 { font-size: 48px; } }

  .br-root h3 { font-size: 20px; font-weight: 700; line-height: 1.4; }
  .br-root h4 { font-size: 18px; font-weight: 600; line-height: 1.5; }

  .br-root::-webkit-scrollbar { width: 5px; }
  .br-root::-webkit-scrollbar-track { background: #f1f5f9; }
  .br-root::-webkit-scrollbar-thumb { background: #1e3a8a; border-radius: 4px; }

  .glass-card {
    background: rgba(255,255,255,0.65);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border: 1px solid rgba(255,255,255,0.5);
    box-shadow: 0 8px 32px rgba(31,38,135,0.12);
  }

  .glass-card-hover {
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .glass-card-hover:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 48px rgba(30,58,138,0.18);
  }

  .nav-link { position: relative; text-decoration: none; }
  .nav-link::after {
    content: ''; position: absolute; bottom: -3px; left: 0;
    width: 0; height: 2px;
    background: #1e3a8a;
    transition: width 0.25s ease;
    border-radius: 2px;
  }
  .nav-link:hover::after, .nav-link.active::after { width: 100%; }

  .ul-hover { position: relative; text-decoration: none; }
  .ul-hover::after {
    content: ''; position: absolute; bottom: -2px; left: 0;
    width: 0; height: 1px; background: #d97706;
    transition: width 0.3s ease;
  }
  .ul-hover:hover::after { width: 100%; }

  @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
  .ticker-track { display: inline-flex; animation: ticker 30s linear infinite; }
  .ticker-track:hover { animation-play-state: paused; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fade-up   { animation: fadeUp 0.7s ease forwards; }
  .fade-up-d1 { animation-delay: 0.1s;  opacity: 0; }
  .fade-up-d2 { animation-delay: 0.25s; opacity: 0; }
  .fade-up-d3 { animation-delay: 0.4s;  opacity: 0; }
  .fade-up-d4 { animation-delay: 0.55s; opacity: 0; }

  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
  .float { animation: float 3.5s ease-in-out infinite; }

  @keyframes gradShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  .anim-gradient {
    background: linear-gradient(270deg, #1e3a8a, #2563eb, #1e40af);
    background-size: 600% 600%;
    animation: gradShift 14s ease infinite;
  }

  @keyframes pulseDot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(1.6)} }
  .pulse-dot { animation: pulseDot 2s ease infinite; }

  .btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 28px; border-radius: 9999px;
    background: #1e3a8a; color: #fff;
    font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 600;
    border: none; cursor: pointer;
    box-shadow: 0 4px 16px rgba(30,58,138,.25);
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  }
  .btn-primary:hover { background: #1d4ed8; transform: scale(1.04); box-shadow: 0 6px 24px rgba(30,58,138,.35); }

  .btn-amber {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 28px; border-radius: 9999px;
    background: #d97706; color: #fff;
    font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 600;
    border: none; cursor: pointer;
    box-shadow: 0 4px 16px rgba(217,119,6,.25);
    transition: background 0.2s, transform 0.15s;
  }
  .btn-amber:hover { background: #b45309; transform: scale(1.04); }

  .btn-outline {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 26px; border-radius: 9999px;
    background: transparent; color: #1e3a8a;
    font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 600;
    border: 2px solid #1e3a8a; cursor: pointer;
    transition: background 0.2s, color 0.2s;
  }
  .btn-outline:hover { background: #1e3a8a; color: #fff; }

  .tag {
    display: inline-block; padding: 4px 12px; border-radius: 9999px;
    font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600;
    letter-spacing: normal; text-transform: uppercase;
  }

  @keyframes slideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
  .mobile-menu-open { animation: slideDown 0.22s ease forwards; }

  .text-gradient {
    background: linear-gradient(135deg, #1e3a8a 0%, #d97706 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .social-icon-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 38px; height: 38px; border-radius: 50%;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.25);
    transition: background 0.2s, transform 0.15s, border-color 0.2s;
    cursor: pointer; text-decoration: none;
  }
  .social-icon-btn:hover {
    background: rgba(255,255,255,0.22);
    border-color: rgba(255,255,255,0.5);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    .hide-mobile { display: none !important; }
    .show-mobile-only { display: flex !important; }
    .col-mobile-1 { grid-template-columns: 1fr !important; }
    .col-mobile-2 { grid-template-columns: 1fr 1fr !important; }
  }

  @media (min-width: 769px) {
    .show-mobile-only { display: none !important; }
  }
`;

function injectStyles(id, css) {
    let s = document.getElementById(id);
    if (!s) {
        s = document.createElement("style");
        s.id = id;
        document.head.appendChild(s);
    }
    if (s.textContent !== css) s.textContent = css;
}

function Counter({ to, suffix = "" }) {
    const [n, setN] = useState(0);
    useEffect(() => {
        let start = null;
        const dur = 1800;
        const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / dur, 1);
            setN(Math.floor(p * to));
            if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [to]);
    return <span>{n}{suffix}</span>;
}

// ─── Social Icons SVG ─────────────────────────────────────────────────────────
function InstagramIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
            <circle cx="12" cy="12" r="4" />
            <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
        </svg>
    );
}

function FacebookIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
        </svg>
    );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar({ page, setPage }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [dropOpen, setDropOpen] = useState(false);

    useEffect(() => {
        const h = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", h);
        return () => window.removeEventListener("scroll", h);
    }, []);

    const primary = [
        { id: "home", label: "Home" },
        { id: "about", label: "About" },
        { id: "academics", label: "Academics" },
        { id: "admissions", label: "Admissions" },
    ];
    const more = [
        { id: "gallery", label: "Gallery" },
        { id: "contact", label: "Contact" },
        { id: "sister", label: "Sister Campus" },
    ];

    return (
        <nav style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
            background: scrolled
                ? "rgba(254,252,232,0.92)"
                : "linear-gradient(to right, #fefce8, #fef9c3, #fefce8)",
            backdropFilter: scrolled ? "blur(14px)" : "none",
            borderBottom: `1px solid ${C.navBorder}`,
            boxShadow: scrolled ? "0 2px 20px rgba(30,58,138,.08)" : "none",
            transition: "background 0.3s, box-shadow 0.3s",
        }}>
            {/* Top blue bar */}
            <div style={{
                background: `linear-gradient(to right, ${C.blue}, #1d4ed8, ${C.blue})`,
                padding: "5px 24px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
                <span style={{ fontFamily: "'Outfit'", fontSize: 12, color: C.amberLight, fontWeight: 700, letterSpacing: "0.05em" }}>
                    BRANCH CAMPUS — EST. 2018
                </span>
                <span style={{ fontFamily: "'DM Sans'", fontSize: 12, color: "rgba(255,255,255,.8)" }}>
                    AY 2025–26 &nbsp;|&nbsp; Admissions Open
                </span>
            </div>

            {/* Main row */}
            <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 68 }}>
                {/* Logo */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }} onClick={() => setPage("home")}>
                    <img
                        src="https://customer-assets.emergentagent.com/job_89f0a926-232e-4f27-8bd9-23de150e4a0b/artifacts/ydf6w6ly_New%20St.%20Franic%20School%20Logo.jpg"
                        alt="New St. Francis School Logo"
                        style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: `2px solid ${C.amberLight}`, boxShadow: "0 2px 10px rgba(30,58,138,.25)", flexShrink: 0 }}
                    />
                    <div>
                        <div style={{ fontFamily: "'Outfit'", fontSize: 18, fontWeight: 700, color: C.blue, lineHeight: 1.2 }}>New St. Francis School</div>
                        <div style={{ fontFamily: "'DM Sans'", fontSize: 11, color: C.amber, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.4 }}>Education is Above All</div>
                        <div style={{ fontFamily: "'DM Sans'", fontSize: 10, color: C.textLow, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1.3 }}>Pithampur Sector-3, Dhar M.P.</div>
                    </div>
                </div>

                {/* Desktop links */}
                <div className="hide-mobile" style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {primary.map(l => (
                        <button key={l.id}
                            className={`nav-link ${page === l.id ? "active" : ""}`}
                            onClick={() => setPage(l.id)}
                            style={{
                                background: page === l.id ? `linear-gradient(135deg, ${C.blue}, #1d4ed8)` : "none",
                                border: "none", cursor: "pointer",
                                fontFamily: "'Outfit'", fontSize: 15, fontWeight: 500,
                                color: page === l.id ? "#fff" : C.blue,
                                padding: "8px 18px", borderRadius: 9999,
                                transition: "background 0.2s, color 0.2s",
                            }}>
                            {l.label}
                        </button>
                    ))}

                    {/* More dropdown */}
                    <div style={{ position: "relative" }}
                        onMouseEnter={() => setDropOpen(true)}
                        onMouseLeave={() => setDropOpen(false)}>
                        <button style={{
                            background: "none", border: "none", cursor: "pointer",
                            fontFamily: "'Outfit'", fontSize: 15, fontWeight: 500,
                            color: C.blue, padding: "8px 18px", borderRadius: 9999,
                            display: "flex", alignItems: "center", gap: 4,
                        }}>
                            More
                            <svg width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.5}
                                style={{ transition: "transform 0.2s", transform: dropOpen ? "rotate(180deg)" : "rotate(0)" }}>
                                <polyline points="2,4 7,10 12,4" />
                            </svg>
                        </button>
                        {dropOpen && (
                            <div className="glass-card mobile-menu-open" style={{
                                position: "absolute", right: 0, top: "calc(100% + 8px)",
                                minWidth: 180, borderRadius: 12, padding: "8px 0", zIndex: 200,
                            }}>
                                {more.map(l => (
                                    <button key={l.id}
                                        onClick={() => { setPage(l.id); setDropOpen(false); }}
                                        style={{
                                            display: "block", width: "100%", textAlign: "left",
                                            background: "none", border: "none", cursor: "pointer",
                                            padding: "10px 20px",
                                            fontFamily: "'DM Sans'", fontSize: 14, fontWeight: 500,
                                            color: C.textMid, transition: "background 0.15s, color 0.15s",
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.background = C.amberPale; e.currentTarget.style.color = C.blue; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = C.textMid; }}>
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right CTAs */}
                <div className="hide-mobile" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <button className="btn-outline" onClick={() => setPage("sister")}
                        style={{ padding: "8px 18px", fontSize: 14, fontWeight: 600, borderWidth: 1.5 }}>
                        ← Main Campus
                    </button>
                    <button className="btn-primary" onClick={() => setPage("admissions")}
                        style={{ padding: "10px 22px", fontSize: 14, fontWeight: 600 }}>
                        Apply Now
                    </button>
                </div>

                {/* Hamburger */}
                <button
                    className="show-mobile-only"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: C.blue, alignItems: "center" }}>
                    <svg width={26} height={26} fill="none" stroke="currentColor" strokeWidth={2}>
                        {mobileOpen
                            ? <><line x1={4} y1={4} x2={20} y2={20} /><line x1={20} y1={4} x2={4} y2={20} /></>
                            : <><line x1={3} y1={7} x2={21} y2={7} /><line x1={3} y1={12} x2={21} y2={12} /><line x1={3} y1={17} x2={21} y2={17} /></>}
                    </svg>
                </button>
            </div>

            {/* Mobile menu */}
            {mobileOpen && (
                <div className="mobile-menu-open" style={{
                    background: "rgba(254,252,232,0.97)", backdropFilter: "blur(12px)",
                    borderTop: `1px solid ${C.navBorder}`, padding: "16px 24px",
                    display: "flex", flexDirection: "column", gap: 4,
                }}>
                    {[...primary, ...more].map(l => (
                        <button key={l.id}
                            onClick={() => { setPage(l.id); setMobileOpen(false); }}
                            style={{
                                background: page === l.id ? C.amberPale : "none",
                                border: "none", cursor: "pointer", textAlign: "left",
                                padding: "11px 16px", borderRadius: 8,
                                fontFamily: "'Outfit'", fontSize: 15, fontWeight: 600,
                                color: page === l.id ? C.blue : C.textMid,
                            }}>
                            {l.label}
                        </button>
                    ))}
                    <button className="btn-primary" onClick={() => { setPage("admissions"); setMobileOpen(false); }}
                        style={{ marginTop: 8, justifyContent: "center" }}>
                        Apply Now
                    </button>
                </div>
            )}
        </nav>
    );
}

// ─── Ticker ───────────────────────────────────────────────────────────────────
function Ticker() {
    const items = [
        "Annual Tech Fest — April 12, 2026",
        "Branch Campus achieves 98% Board Results 🏆",
        "New STEM Lab inaugurated — Feb 2026",
        "Robotics Club wins State Championship",
        "Admissions for AY 2026–27 now open",
        "Cultural Exchange Program with Pune Campus",
        "PTM Scheduled — March 28, 2026",
    ];
    const doubled = [...items, ...items];
    return (
        <div style={{ background: C.amberPale, borderTop: `1px solid ${C.amberLight}44`, borderBottom: `1px solid ${C.amberLight}44`, padding: "9px 0", overflow: "hidden" }}>
            <div className="ticker-track" style={{ gap: 60 }}>
                {doubled.map((item, i) => (
                    <span key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "'DM Sans'", fontSize: 14, fontWeight: 500, color: C.textMid, whiteSpace: "nowrap" }}>
                        <span style={{ color: C.amber, fontSize: 10 }}>◆</span>
                        {item}
                    </span>
                ))}
            </div>
        </div>
    );
}

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
function HomePage({ setPage }) {
    const stats = [
        { val: 680, suf: "+", label: "Students Enrolled" },
        { val: 98, suf: "%", label: "Board Pass Rate" },
        { val: 45, suf: "+", label: "Faculty Members" },
        { val: 8, suf: "", label: "Years of Excellence" },
    ];

    const highlights = [
        { tag: "STEM", color: C.blue, bg: "#dbeafe", title: "Advanced Science Labs", desc: "Fully equipped physics, chemistry & bio labs with digital integration." },
        { tag: "ARTS", color: C.amber, bg: C.amberPale, title: "Creative Arts Studio", desc: "Painting, sculpture, music and performing arts under expert mentors." },
        { tag: "SPORTS", color: "#16a34a", bg: "#dcfce7", title: "Olympic-Grade Grounds", desc: "Cricket, basketball, football & athletics with certified coaches." },
        { tag: "TECH", color: "#7c3aed", bg: "#ede9fe", title: "AI & Robotics Club", desc: "Students compete at national level robotics and coding olympiads." },
        { tag: "LANG", color: C.sky, bg: "#e0f2fe", title: "English & French Proficiency", desc: "Dedicated language lab and exchange programs." },
        { tag: "LEAD", color: "#db2777", bg: "#fce7f3", title: "Leadership Council", desc: "Student-run committees building real-world governance skills." },
    ];

    return (
        <div>
            {/* Hero */}
            <section style={{
                minHeight: "100vh",
                backgroundImage: `linear-gradient(160deg, rgba(10,25,70,0.85) 0%, rgba(30,58,138,0.75) 40%, rgba(15,23,42,0.9) 100%), url("/images/Branchimages/b_56.jpeg")`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex", alignItems: "center",
                paddingTop: 112, position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", top: -100, right: -100, width: 600, height: 600, borderRadius: "50%", background: `radial-gradient(circle, ${C.amberLight}30 0%, transparent 70%)`, pointerEvents: "none" }} />
                <div style={{ position: "absolute", bottom: -80, left: -80, width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${C.blueLight}20 0%, transparent 70%)`, pointerEvents: "none" }} />

                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 80px", width: "100%", textAlign: "center", position: "relative", zIndex: 1 }}>
                    <div className="fade-up fade-up-d1" style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        background: "rgba(255,255,255,0.12)",
                        backdropFilter: "blur(8px)",
                        border: `1px solid ${C.amberLight}99`,
                        borderRadius: 9999, padding: "6px 18px", marginBottom: 28
                    }}>
                        <span className="pulse-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: C.amberLight, display: "inline-block" }} />
                        <span style={{ fontFamily: "'Outfit'", fontSize: 12, color: C.amberLight, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                            Branch Campus — Admissions Open
                        </span>
                    </div>

                    <h1 className="fade-up fade-up-d2" style={{
                        fontFamily: "'Outfit'", fontSize: "clamp(48px,7vw,72px)",
                        fontWeight: 700, lineHeight: 1.15,
                        maxWidth: 800, margin: "0 auto 24px",
                        color: "#ffffff",
                        textShadow: "0 2px 20px rgba(0,0,0,0.4)"
                    }}>
                        Where Curiosity<br />
                        <span style={{
                            background: "linear-gradient(135deg, #ffffff 0%, #fbbf24 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text"
                        }}>
                            Meets Excellence.
                        </span>
                    </h1>

                    <p className="fade-up fade-up-d3" style={{
                        fontSize: 20, color: "rgba(255,255,255,0.82)",
                        maxWidth: 540, margin: "0 auto 40px", lineHeight: 1.625,
                        textShadow: "0 1px 8px rgba(0,0,0,0.3)"
                    }}>
                        St. Francis Global Campus — the modern branch of the legacy institution — brings a progressive, research-driven education model to the next generation of thinkers.
                    </p>

                    <div className="fade-up fade-up-d4" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
                        <button className="btn-primary" onClick={() => setPage("admissions")}
                            style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.35)" }}>
                            Explore Admissions →
                        </button>
                        <button onClick={() => setPage("about")} style={{
                            display: "inline-flex", alignItems: "center", gap: 8,
                            padding: "11px 26px", borderRadius: 9999,
                            background: "rgba(255,255,255,0.12)",
                            backdropFilter: "blur(8px)",
                            color: "#ffffff",
                            fontFamily: "'Outfit'", fontSize: 18, fontWeight: 600,
                            border: "2px solid rgba(255,255,255,0.6)", cursor: "pointer",
                            transition: "background 0.2s, border-color 0.2s",
                        }}
                            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.22)"; e.currentTarget.style.borderColor = "#fff"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)"; }}>
                            Our Story
                        </button>
                        <button onClick={() => setPage("sister")} style={{
                            background: "none", border: "none", cursor: "pointer",
                            display: "flex", alignItems: "center", gap: 8,
                            fontFamily: "'Outfit'", fontSize: 18, fontWeight: 600,
                            color: C.amberLight,
                            textShadow: "0 1px 8px rgba(0,0,0,0.4)"
                        }}>
                            ⇄ Visit Main Campus
                        </button>
                    </div>

                    <div className="float" style={{ marginTop: 60, display: "inline-block" }}>
                        <div style={{
                            background: "rgba(255,255,255,0.12)",
                            backdropFilter: "blur(18px)",
                            WebkitBackdropFilter: "blur(18px)",
                            border: "1px solid rgba(255,255,255,0.25)",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                            borderRadius: 20, padding: "18px 32px",
                            display: "inline-flex", gap: 32
                        }}>
                            {stats.slice(0, 2).map((s, i) => (
                                <div key={i} style={{ textAlign: "center" }}>
                                    <div style={{ fontFamily: "'Outfit'", fontSize: 36, fontWeight: 700, color: C.amberLight }}>
                                        <Counter to={s.val} suffix={s.suf} />
                                    </div>
                                    <div style={{ fontSize: 16, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats bar */}
            <section className="anim-gradient" style={{ padding: "32px 0" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: "repeat(4,1fr)" }} className="col-mobile-2">
                    {stats.map((s, i) => (
                        <div key={i} style={{ textAlign: "center", borderRight: i < 3 ? "1px solid rgba(255,255,255,.2)" : "none", padding: "0 16px" }}>
                            <div style={{ fontFamily: "'Outfit'", fontSize: 36, fontWeight: 700, color: "#fff" }}>
                                <Counter to={s.val} suffix={s.suf} />
                            </div>
                            <div style={{ fontFamily: "'DM Sans'", fontSize: 16, color: "rgba(255,255,255,.9)", fontWeight: 500, marginTop: 4 }}>{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            <Ticker />

            {/* Highlights */}
            <section style={{ padding: "100px 0", background: C.bg }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
                    <div style={{ textAlign: "center", marginBottom: 60 }}>
                        <span className="tag" style={{ background: C.amberPale, color: C.amber, marginBottom: 16, display: "inline-block" }}>What Sets Us Apart</span>
                        <h2 style={{ fontFamily: "'Outfit'", fontSize: "clamp(36px,4vw,48px)", fontWeight: 700, color: C.blue }}>Campus <span style={{ color: C.amber }}>Highlights</span></h2>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }} className="col-mobile-1">
                        {highlights.map((h, i) => (
                            <div key={i} className="glass-card glass-card-hover" style={{ borderRadius: 20, padding: "36px 28px" }}>
                                <span className="tag" style={{ background: h.bg, color: h.color, marginBottom: 20, display: "inline-block" }}>{h.tag}</span>
                                <h3 style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.textHi, marginBottom: 8 }}>{h.title}</h3>
                                <p style={{ fontSize: 16, lineHeight: 1.625, color: C.textMid }}>{h.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div style={{ textAlign: "center", marginTop: 48 }}>
                        <button className="btn-outline" onClick={() => setPage("academics")}>View All Programs →</button>
                    </div>
                </div>
            </section>

            {/* CTA Band */}
            <section style={{ background: `linear-gradient(135deg, ${C.blue} 0%, #1d4ed8 50%, #1e40af 100%)`, padding: "80px 0" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 32 }}>
                    <div>
                        <h2 style={{ fontFamily: "'Outfit'", fontSize: "clamp(36px,4vw,48px)", fontWeight: 700, color: "#fff", marginBottom: 12 }}>
                            Ready to join<br /><span style={{ color: C.amberLight }}>our campus?</span>
                        </h2>
                        <p style={{ color: "rgba(255,255,255,.75)", fontSize: 20, maxWidth: 400 }}>Applications for AY 2026–27 are now open. Limited seats in each stream.</p>
                    </div>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                        <button className="btn-amber" onClick={() => setPage("admissions")}>Apply Now →</button>
                        <button onClick={() => setPage("contact")} style={{ background: "rgba(255,255,255,.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,.3)", borderRadius: 9999, padding: "12px 28px", color: "#fff", fontFamily: "'Outfit'", fontSize: 18, fontWeight: 600, cursor: "pointer" }}>
                            Schedule a Visit
                        </button>
                    </div>
                </div>
            </section>

            {/* Sister campus */}
            <section style={{ background: `linear-gradient(135deg, ${C.amberPale} 0%, #fffbeb 60%, #fef9c3 100%)`, borderTop: `1px solid ${C.amberLight}44`, padding: "44px 0" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <div style={{ width: 52, height: 52, borderRadius: 12, background: `linear-gradient(135deg, ${C.blue}, ${C.blueLight})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <span style={{ fontSize: 26 }}>🏫</span>
                        </div>
                        <div>
                            <div style={{ fontFamily: "'Outfit'", fontSize: 18, color: C.blue, fontWeight: 700 }}>New St. Francis School — Main Campus</div>
                            <div style={{ fontFamily: "'DM Sans'", fontSize: 12, color: C.amber, fontWeight: 400, textTransform: "uppercase" }}>Our Sister Institution · Est. 2002 · 1100+ Students</div>
                        </div>
                    </div>
                    <button className="btn-amber" onClick={() => setPage("sister")}>Visit Main Campus →</button>
                </div>
            </section>
        </div>
    );
}

// ─── ABOUT PAGE ───────────────────────────────────────────────────────────────
function AboutPage() {
    const timeline = [
        { year: "2018", title: "Branch Founded", desc: "Opened with 120 students as the modern extension of the main institution." },
        { year: "2019", title: "STEM Lab Launch", desc: "State-of-the-art science and technology centre inaugurated." },
        { year: "2021", title: "Best New School Award", desc: "Received district-level recognition for academic innovation." },
        { year: "2023", title: "Robotics Championship", desc: "Students won first prize at State Robotics Olympiad." },
        { year: "2025", title: "98% Board Results", desc: "Highest ever pass percentage achieved in Class X & XII." },
        { year: "2026", title: "Global Partnership", desc: "Signed MoU with international school in Singapore for exchange." },
    ];

    // ── Updated Leadership Team ──────────────────────────────────────────────
    const team = [
        {
            name: "Shivendra Singh",
            role: "Secretary / Trustee",
            qual: "School Administration",
            vision: "Empowering every student to become a responsible and visionary citizen of tomorrow."
        },
        {
            name: "Mahendra Singh",
            role: "Director",
            qual: "Educational Leadership",
            vision: "Building institutions that stand as pillars of knowledge, integrity, and lifelong learning."
        },
        {
            name: "Sushma Singh",
            role: "Principal",
            qual: "B.Sc, B.Ed",
            vision: "Nurturing young minds with compassion, discipline, and a passion for excellence."
        },
    ];

    return (
        <div style={{ paddingTop: 112 }}>
            <section style={{ background: `linear-gradient(160deg, ${C.blue} 0%, #1d4ed8 60%, #1e40af 100%)`, padding: "80px 0 70px" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
                    <span className="tag" style={{ background: `${C.amberLight}33`, color: C.amberLight, marginBottom: 20, display: "inline-block" }}>Our Story</span>
                    <h1 style={{ fontFamily: "'Outfit'", fontSize: "clamp(48px,6vw,72px)", fontWeight: 700, color: "#fff", maxWidth: 700, margin: "0 auto 20px", lineHeight: 1.15 }}>
                        Built for the <span style={{ color: C.amberLight }}>Thinkers</span> of Tomorrow
                    </h1>
                    <p style={{ fontSize: 20, color: "rgba(255,255,255,.8)", maxWidth: 560, margin: "0 auto", lineHeight: 1.625 }}>
                        St. Francis Global Campus was established in 2018 as the progressive branch of the storied New St. Francis School — delivering a rigorous, holistic education combining 24-year legacy with cutting-edge pedagogy.
                    </p>
                </div>
            </section>

            <section style={{ padding: "100px 0", background: C.bg }}>
                <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px" }}>
                    <h2 style={{ fontFamily: "'Outfit'", fontSize: "clamp(36px,4vw,48px)", fontWeight: 700, color: C.blue, marginBottom: 56, textAlign: "center" }}>Our <span style={{ color: C.amber }}>Journey</span></h2>
                    <div style={{ position: "relative" }}>
                        <div style={{ position: "absolute", left: 32, top: 0, bottom: 0, width: 2, background: `linear-gradient(${C.blue}, ${C.amberLight})` }} />
                        {timeline.map((t, i) => (
                            <div key={i} style={{ display: "flex", gap: 32, marginBottom: 40, alignItems: "flex-start" }}>
                                <div style={{ flexShrink: 0, position: "relative", zIndex: 1 }}>
                                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: i % 2 === 0 ? C.blue : C.amber, border: `3px solid ${C.bg}`, boxShadow: `0 0 0 3px ${i % 2 === 0 ? C.blue + "44" : C.amber + "44"}`, marginLeft: 22 }} />
                                </div>
                                <div className="glass-card glass-card-hover" style={{ borderRadius: 16, padding: "24px 28px", flex: 1 }}>
                                    <span style={{ fontFamily: "'Outfit'", fontSize: 14, color: C.amber, fontWeight: 600, display: "block", marginBottom: 6 }}>{t.year}</span>
                                    <h3 style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.blue, marginBottom: 8 }}>{t.title}</h3>
                                    <p style={{ color: C.textMid, fontSize: 16, lineHeight: 1.625 }}>{t.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Leadership Team — updated to 3 members with vision ── */}
            <section style={{ padding: "80px 0 100px", background: `linear-gradient(135deg, #eff6ff 0%, ${C.amberPale} 100%)` }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
                    <h2 style={{ fontFamily: "'Outfit'", fontSize: "clamp(36px,4vw,48px)", fontWeight: 700, color: C.blue, marginBottom: 56, textAlign: "center" }}>Leadership <span style={{ color: C.amber }}>Team</span></h2>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }} className="col-mobile-1">
                        {team.map((m, i) => (
                            <div key={i} className="glass-card glass-card-hover" style={{ borderRadius: 20, padding: "36px 28px", textAlign: "center" }}>
                                {/* Avatar circle */}
                                <div style={{ width: 72, height: 72, borderRadius: "50%", margin: "0 auto 20px", background: `linear-gradient(135deg, ${C.blue}, ${C.blueLight})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 20px rgba(30,58,138,.3)` }}>
                                    <span style={{ fontFamily: "'Outfit'", fontSize: 28, color: "#fff", fontWeight: 900 }}>{m.name[0]}</span>
                                </div>
                                {/* Name */}
                                <div style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.blue, marginBottom: 4 }}>{m.name}</div>
                                {/* Role */}
                                <div style={{ fontFamily: "'DM Sans'", fontSize: 12, color: C.amber, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{m.role}</div>
                                {/* Qualification */}
                                <div style={{ fontSize: 14, color: C.textMid, marginBottom: 16 }}>{m.qual}</div>
                                {/* Divider */}
                                <div style={{ width: 40, height: 2, background: `linear-gradient(to right, ${C.blue}, ${C.amber})`, borderRadius: 2, margin: "0 auto 16px" }} />
                                {/* Vision one-liner */}
                                <p style={{ fontSize: 14, color: C.textMid, lineHeight: 1.7, fontStyle: "italic" }}>
                                    "{m.vision}"
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

// ─── ACADEMICS PAGE ───────────────────────────────────────────────────────────
function AcademicsPage() {
    const streams = [
        { name: "Science Stream", board: "CBSE", grades: "IX–XII", subjects: ["Physics", "Chemistry", "Biology / Math", "Computer Science", "English"], accent: C.blue, bg: "#dbeafe" },
        { name: "Commerce Stream", board: "CBSE", grades: "XI–XII", subjects: ["Accounts", "Business Studies", "Economics", "Mathematics", "English"], accent: C.amber, bg: C.amberPale },
        { name: "Arts Stream", board: "CBSE", grades: "XI–XII", subjects: ["History", "Political Science", "Geography", "Psychology", "English"], accent: "#7c3aed", bg: "#ede9fe" },
        { name: "Primary Wing", board: "CBSE", grades: "I–VIII", subjects: ["English", "Hindi", "Math", "Science", "Social Studies", "Art & Craft"], accent: "#16a34a", bg: "#dcfce7" },
    ];
    const programs = [
        { icon: "🤖", title: "Robotics & AI Club", desc: "Weekly sessions, national competitions, real-world projects." },
        { icon: "🎭", title: "Drama & Fine Arts", desc: "Annual productions, art exhibitions, inter-school fests." },
        { icon: "⚽", title: "Sports Academy", desc: "Cricket, football, basketball with professional coaching." },
        { icon: "🌐", title: "Language Lab", desc: "English proficiency, French basics, public speaking." },
        { icon: "🔬", title: "Research Program", desc: "Guided research projects for senior students." },
        { icon: "🎵", title: "Music & Performing Arts", desc: "Keyboard, guitar, Indian classical, western vocals." },
    ];

    return (
        <div style={{ paddingTop: 112 }}>
            <section style={{ background: `linear-gradient(160deg, ${C.blue} 0%, #1d4ed8 60%, #1e40af 100%)`, padding: "80px 0 70px" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
                    <span className="tag" style={{ background: `${C.amberLight}33`, color: C.amberLight, marginBottom: 16, display: "inline-block" }}>Curriculum</span>
                    <h1 style={{ fontFamily: "'Outfit'", fontSize: "clamp(48px,6vw,72px)", fontWeight: 700, color: "#fff", marginBottom: 16, lineHeight: 1.15 }}>Academics <span style={{ color: C.amberLight }}>&amp; Programs</span></h1>
                    <p style={{ fontSize: 20, color: "rgba(255,255,255,.8)", maxWidth: 520, margin: "0 auto", lineHeight: 1.625 }}>CBSE-affiliated curriculum with a strong emphasis on critical thinking, research, and real-world application.</p>
                </div>
            </section>

            <section style={{ padding: "80px 0", background: C.bg }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
                    <h2 style={{ fontFamily: "'Outfit'", fontSize: "clamp(36px,4vw,48px)", fontWeight: 700, color: C.blue, marginBottom: 40, textAlign: "center" }}>Academic <span style={{ color: C.amber }}>Streams</span></h2>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 20 }} className="col-mobile-1">
                        {streams.map((s, i) => (
                            <div key={i} className="glass-card glass-card-hover" style={{ borderRadius: 20, padding: "36px 32px", borderLeft: `4px solid ${s.accent}` }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                                    <h3 style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.blue }}>{s.name}</h3>
                                    <div style={{ textAlign: "right" }}>
                                        <span className="tag" style={{ background: s.bg, color: s.accent, display: "block", marginBottom: 4 }}>{s.board}</span>
                                        <span style={{ fontSize: 12, color: C.textLow }}>Grades {s.grades}</span>
                                    </div>
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                    {s.subjects.map((sub, j) => (
                                        <span key={j} style={{ padding: "5px 14px", background: s.bg, border: `1px solid ${s.accent}33`, borderRadius: 9999, fontSize: 14, fontWeight: 600, color: s.accent }}>{sub}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section style={{ padding: "80px 0 100px", background: `linear-gradient(135deg, #eff6ff 0%, ${C.amberPale} 100%)` }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
                    <h2 style={{ fontFamily: "'Outfit'", fontWeight: 700, fontSize: "clamp(36px,3vw,48px)", color: C.blue, marginBottom: 40, textAlign: "center" }}>Co-Curricular <span style={{ color: C.amber }}>Activities</span></h2>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }} className="col-mobile-1">
                        {programs.map((p, i) => (
                            <div key={i} className="glass-card glass-card-hover" style={{ borderRadius: 20, padding: "28px 24px", display: "flex", gap: 16, alignItems: "flex-start" }}>
                                <span style={{ fontSize: 36, flexShrink: 0 }}>{p.icon}</span>
                                <div>
                                    <div style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.blue, marginBottom: 8 }}>{p.title}</div>
                                    <div style={{ fontSize: 16, color: C.textMid, lineHeight: 1.625 }}>{p.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

// ─── ADMISSIONS PAGE ──────────────────────────────────────────────────────────
function AdmissionsPage() {
    const [form, setForm] = useState({ name: "", grade: "", phone: "", email: "" });
    const [submitted, setSubmitted] = useState(false);

    const steps = [
        { n: "01", title: "Fill Online Form", desc: "Complete the application form below with student and parent details." },
        { n: "02", title: "Document Submission", desc: "Submit birth certificate, previous marksheets, and photos." },
        { n: "03", title: "Entrance Assessment", desc: "Appear for a short aptitude assessment (Grade V and above)." },
        { n: "04", title: "Interview & Confirmation", desc: "Brief parent-child interview followed by seat confirmation and fee payment." },
    ];
    const fees = [
        { class: "Pre-Primary (Nursery - KG)", reg: "₹500", admission: "₹8,000", annual: "₹32,000" },
        { class: "Primary (I - V)", reg: "₹500", admission: "₹10,000", annual: "₹38,000" },
        { class: "Middle (VI - VIII)", reg: "₹750", admission: "₹12,000", annual: "₹44,000" },
        { class: "Secondary (IX - X)", reg: "₹750", admission: "₹14,000", annual: "₹52,000" },
        { class: "Senior Secondary (XI - XII)", reg: "₹1,000", admission: "₹16,000", annual: "₹58,000" },
    ];

    return (
        <div style={{ paddingTop: 112 }}>
            <section style={{ background: `linear-gradient(160deg, ${C.blue} 0%, #1d4ed8 60%, #1e40af 100%)`, padding: "80px 0 70px" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
                    <span className="tag" style={{ background: `${C.amberLight}33`, color: C.amberLight, marginBottom: 16, display: "inline-block" }}>AY 2026–27</span>
                    <h1 style={{ fontFamily: "'Outfit'", fontWeight: 700, fontSize: "clamp(48px,6vw,72px)", color: "#fff", marginBottom: 16 }}>Admissions <span style={{ color: C.amberLight }}>Open</span></h1>
                    <p style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.625, color: "rgba(255,255,255,.8)", maxWidth: 520, margin: "0 auto" }}>Join the campus that blends 24 years of academic legacy with a progressive, future-ready curriculum.</p>
                </div>
            </section>

            <section style={{ padding: "80px 0", background: C.bg }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
                    <h2 style={{ fontFamily: "'Outfit'", fontWeight: 700, fontSize: "clamp(36px,3vw,48px)", color: C.blue, marginBottom: 48, textAlign: "center" }}>Admission <span style={{ color: C.amber }}>Process</span></h2>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20 }} className="col-mobile-2">
                        {steps.map((s, i) => (
                            <div key={i} className="glass-card glass-card-hover" style={{ borderRadius: 20, padding: "32px 24px", textAlign: "center" }}>
                                <div style={{ fontFamily: "'Outfit'", fontSize: 36, fontWeight: 700, color: `${C.blue}22`, marginBottom: 16 }}>{s.n}</div>
                                <h3 style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.blue, marginBottom: 12 }}>{s.title}</h3>
                                <p style={{ fontSize: 14, color: C.textMid, lineHeight: 1.625 }}>{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section style={{ padding: "60px 0 80px", background: `linear-gradient(135deg, #eff6ff 0%, ${C.amberPale} 100%)` }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
                    <h2 style={{ fontFamily: "'Outfit'", fontWeight: 700, fontSize: "clamp(36px,3vw,48px)", color: C.blue, marginBottom: 40, textAlign: "center" }}>Fee <span style={{ color: C.amber }}>Structure</span></h2>
                    <div className="glass-card" style={{ borderRadius: 20, overflow: "hidden" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", background: `linear-gradient(135deg, ${C.blue}, #1d4ed8)`, padding: "16px 24px" }}>
                            {["Class / Grade", "Registration", "Admission", "Annual"].map((h, i) => (
                                <span key={i} style={{ fontFamily: "'Outfit'", fontSize: 14, color: i === 3 ? C.amberLight : "#fff", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>{h}</span>
                            ))}
                        </div>
                        {fees.map((f, i) => (
                            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", padding: "15px 24px", borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : `${C.bgSubtle}88` }}>
                                <span style={{ fontSize: 14, color: C.textHi, fontWeight: 400 }}>{f.class}</span>
                                <span style={{ fontSize: 14, color: C.textMid }}>{f.reg}</span>
                                <span style={{ fontSize: 14, color: C.textMid }}>{f.admission}</span>
                                <span style={{ fontSize: 14, color: C.amber, fontWeight: 700 }}>{f.annual}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section style={{ padding: "80px 0 100px", background: C.bg }}>
                <div style={{ maxWidth: 660, margin: "0 auto", padding: "0 24px" }}>
                    <h2 style={{ fontFamily: "'Outfit'", fontWeight: 700, fontSize: "clamp(36px,3vw,48px)", color: C.blue, marginBottom: 8, textAlign: "center" }}>Quick <span style={{ color: C.amber }}>Enquiry</span></h2>
                    <p style={{ fontSize: 16, color: C.textMid, marginBottom: 40, textAlign: "center" }}>We'll get back to you within 24 hours.</p>
                    {!submitted ? (
                        <div className="glass-card" style={{ borderRadius: 24, padding: "40px 36px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                {[
                                    { key: "name", label: "Student Full Name", type: "text" },
                                    { key: "grade", label: "Applying for Grade", type: "text" },
                                    { key: "phone", label: "Parent Phone Number", type: "tel" },
                                    { key: "email", label: "Email Address (optional)", type: "email" },
                                ].map(field => (
                                    <div key={field.key}>
                                        <label style={{ fontFamily: "'Outfit'", fontSize: 12, color: C.textLow, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", marginBottom: 8 }}>{field.label}</label>
                                        <input type={field.type} value={form[field.key]}
                                            onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                                            style={{ width: "100%", background: "#fff", border: `1.5px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", color: C.textHi, fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans'", outline: "none" }}
                                            onFocus={e => e.target.style.borderColor = C.blue}
                                            onBlur={e => e.target.style.borderColor = C.border} />
                                    </div>
                                ))}
                                <button className="btn-primary" onClick={() => { if (form.name && form.grade && form.phone) setSubmitted(true); }}
                                    style={{ marginTop: 8, justifyContent: "center" }}>
                                    Submit Enquiry →
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="glass-card" style={{ borderRadius: 24, padding: "48px 36px", textAlign: "center", border: `1px solid ${C.amberLight}66` }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                            <h3 style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.blue, marginBottom: 12 }}>Enquiry Received!</h3>
                            <p style={{ color: C.textMid, lineHeight: 1.7 }}>Thank you, <strong>{form.name}</strong>. Our admissions team will contact you at {form.phone} within 24 hours.</p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

// ─── GALLERY PAGE ─────────────────────────────────────────────────────────────
function GalleryPage() {
    const [activeIndex, setActiveIndex] = useState(null);
    const [zoom, setZoom] = useState(1);

    const images = Array.from({ length: 55 }, (_, i) =>
        i === 0
            ? "/images/Branchimages/b.jpeg"
            : `/images/Branchimages/b_${String(i + 1).padStart(2, "0")}.jpeg`
    );

    useEffect(() => {
        const handleKey = (e) => {
            if (activeIndex === null) return;
            if (e.key === "ArrowRight") setActiveIndex((activeIndex + 1) % images.length);
            if (e.key === "ArrowLeft") setActiveIndex((activeIndex - 1 + images.length) % images.length);
            if (e.key === "Escape") setActiveIndex(null);
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [activeIndex]);

    const shareImage = async (url) => {
        if (navigator.share) {
            await navigator.share({ title: "Campus Gallery", url });
        } else {
            navigator.clipboard.writeText(url);
            alert("Image link copied!");
        }
    };

    return (
        <div style={{ paddingTop: 110 }}>
            <section style={{ background: `linear-gradient(160deg, ${C.blue} 0%, #1d4ed8 60%, #1e40af 100%)`, padding: "80px 0 70px", textAlign: "center" }}>
                <span className="tag" style={{ background: `${C.amberLight}33`, color: C.amberLight, marginBottom: 16, display: "inline-block" }}>Our Campus</span>
                <h1 style={{ fontFamily: "'Outfit'", fontSize: "clamp(48px,6vw,72px)", fontWeight: 700, color: "#fff", lineHeight: 1.15 }}>
                    Campus <span style={{ color: C.amberLight }}>Gallery</span>
                </h1>
            </section>

            <section style={{ padding: "60px 0", background: C.bg }}>
                <div style={{
                    maxWidth: 1200, margin: "auto",
                    display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
                    gap: 20, padding: "0 20px"
                }}>
                    {images.map((img, i) => (
                        <div key={i}
                            style={{ overflow: "hidden", borderRadius: 16, cursor: "pointer", boxShadow: "0 8px 30px rgba(0,0,0,0.08)", aspectRatio: "4/3" }}
                            onClick={() => { setActiveIndex(i); setZoom(1); }}>
                            <img src={img} alt={`Campus photo ${i + 1}`}
                                style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s" }}
                                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"} />
                        </div>
                    ))}
                </div>
            </section>

            {activeIndex !== null && (
                <div style={{
                    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                    background: "rgba(0,0,0,0.95)", display: "flex", alignItems: "center",
                    justifyContent: "center", zIndex: 999
                }}
                    onClick={(e) => { if (e.target === e.currentTarget) setActiveIndex(null); }}>
                    <img src={images[activeIndex]} alt=""
                        style={{ maxHeight: "80%", maxWidth: "80%", transform: `scale(${zoom})`, transition: "transform 0.3s", borderRadius: 12 }} />

                    <div style={{ position: "absolute", bottom: 30, display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                        {[
                            { label: "🔍+", action: () => setZoom(z => Math.min(z + 0.2, 3)) },
                            { label: "🔍−", action: () => setZoom(z => Math.max(1, z - 0.2)) },
                        ].map((btn, i) => (
                            <button key={i} onClick={btn.action}
                                style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 9999, padding: "10px 20px", color: "#fff", fontFamily: "'Outfit'", fontSize: 16, cursor: "pointer" }}>
                                {btn.label}
                            </button>
                        ))}
                        <a href={images[activeIndex]} download style={{ textDecoration: "none" }}>
                            <button style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 9999, padding: "10px 20px", color: "#fff", fontFamily: "'Outfit'", fontSize: 16, cursor: "pointer" }}>
                                ⬇ Download
                            </button>
                        </a>
                        <button onClick={() => shareImage(images[activeIndex])}
                            style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 9999, padding: "10px 20px", color: "#fff", fontFamily: "'Outfit'", fontSize: 16, cursor: "pointer" }}>
                            📤 Share
                        </button>
                        <button onClick={() => setActiveIndex(null)}
                            style={{ background: "rgba(239,68,68,0.7)", border: "none", borderRadius: 9999, padding: "10px 20px", color: "#fff", fontFamily: "'Outfit'", fontSize: 16, cursor: "pointer" }}>
                            ✖ Close
                        </button>
                    </div>

                    <button onClick={() => setActiveIndex((activeIndex - 1 + images.length) % images.length)}
                        style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", fontSize: 40, background: "rgba(255,255,255,0.1)", color: "#fff", border: "none", borderRadius: "50%", width: 56, height: 56, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        ❮
                    </button>
                    <button onClick={() => setActiveIndex((activeIndex + 1) % images.length)}
                        style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", fontSize: 40, background: "rgba(255,255,255,0.1)", color: "#fff", border: "none", borderRadius: "50%", width: 56, height: 56, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        ❯
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── CONTACT PAGE ─────────────────────────────────────────────────────────────
function ContactPage() {
    const info = [
        { icon: "📍", label: "Address", val: "Plot 24, Sector 7, Green Valley, Nagpur — 440010" },
        { icon: "📞", label: "Phone", val: "+91 97554 02604 | +91 98277 82604" },
        { icon: "✉️", label: "Email", val: "info@sfgc.edu.in" },
        { icon: "🕐", label: "Office Hours", val: "Mon–Sat: 8:00 AM – 4:00 PM" },
    ];

    return (
        <div style={{ paddingTop: 112 }}>
            <section style={{ background: `linear-gradient(160deg, ${C.blue} 0%, #1d4ed8 60%, #1e40af 100%)`, padding: "80px 0 70px" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
                    <h1 style={{ fontFamily: "'Outfit'", fontWeight: 700, fontSize: "clamp(48px,6vw,72px)", color: "#fff", marginBottom: 16 }}>Get In <span style={{ color: C.amberLight }}>Touch</span></h1>
                    <p style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.625, color: "rgba(255,255,255,.8)", maxWidth: 440, margin: "0 auto" }}>We'd love to show you around. Book a campus tour or drop us a message.</p>
                </div>
            </section>

            <section style={{ padding: "80px 0 60px", background: `linear-gradient(135deg, #eff6ff 0%, ${C.amberPale} 100%)` }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 40, alignItems: "start" }} className="col-mobile-1">
                    <div>
                        <h2 style={{ fontFamily: "'Outfit'", fontSize: 30, fontWeight: 700, color: C.blue, marginBottom: 36 }}>Contact Information</h2>
                        {info.map((item, i) => (
                            <div key={i} className="glass-card" style={{ borderRadius: 16, padding: "18px 20px", marginBottom: 16, display: "flex", gap: 18, alignItems: "flex-start" }}>
                                <span style={{ fontSize: 28 }}>{item.icon}</span>
                                <div>
                                    <div style={{ fontFamily: "'Outfit'", fontSize: 12, color: C.amber, fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>{item.label}</div>
                                    <div style={{ fontSize: 16, fontWeight: 500, color: C.textHi }}>{item.val}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="glass-card" style={{ borderRadius: 24, padding: "40px 36px" }}>
                        <h3 style={{ fontFamily: "'Outfit'", fontSize: 30, fontWeight: 700, color: C.blue, marginBottom: 28 }}>Send a Message</h3>
                        {["Your Name", "Email Address", "Subject"].map((pl, i) => (
                            <input key={i} placeholder={pl}
                                style={{ display: "block", width: "100%", background: "#fff", border: `1.5px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", color: C.textHi, fontSize: 14, fontFamily: "'DM Sans'", marginBottom: 14, outline: "none" }}
                                onFocus={e => e.target.style.borderColor = C.blue}
                                onBlur={e => e.target.style.borderColor = C.border} />
                        ))}
                        <textarea placeholder="Your message…" rows={4}
                            style={{ display: "block", width: "100%", background: "#fff", border: `1.5px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", color: C.textHi, fontSize: 14, fontFamily: "'DM Sans'", marginBottom: 20, outline: "none", resize: "vertical" }}
                            onFocus={e => e.target.style.borderColor = C.blue}
                            onBlur={e => e.target.style.borderColor = C.border} />
                        <button className="btn-primary" style={{ width: "100%", justifyContent: "center" }}>Send Message →</button>
                    </div>
                </div>
            </section>

            {/* ── Google Maps Location Section ── */}
            <section style={{ padding: "0 0 100px", background: `linear-gradient(135deg, #eff6ff 0%, ${C.amberPale} 100%)` }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
                    <div style={{ textAlign: "center", marginBottom: 36 }}>
                        <h2 style={{ fontFamily: "'Outfit'", fontSize: "clamp(28px,3vw,36px)", fontWeight: 700, color: C.blue }}>
                            Find Us on the <span style={{ color: C.amber }}>Map</span>
                        </h2>
                        <p style={{ fontSize: 15, color: C.textMid, marginTop: 8 }}>New St. Francis School — Pithampur, Sector 3, Dhar M.P.</p>
                    </div>

                    <div className="glass-card" style={{ borderRadius: 24, overflow: "hidden", boxShadow: "0 12px 40px rgba(30,58,138,0.15)" }}>
                        {/* Map header bar */}
                        <div style={{
                            background: `linear-gradient(135deg, ${C.blue}, #1d4ed8)`,
                            padding: "14px 24px",
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontSize: 20 }}>📍</span>
                                <span style={{ fontFamily: "'Outfit'", fontSize: 15, fontWeight: 600, color: "#fff" }}>
                                    New St. Francis School, Pithampur Sector-3, Dhar M.P.
                                </span>
                            </div>
                            <a
                                href="https://maps.app.goo.gl/ojcW58qZFrg7jrCF6?g_st=iw"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    background: C.amber, color: "#fff",
                                    padding: "7px 18px", borderRadius: 9999,
                                    fontFamily: "'Outfit'", fontSize: 13, fontWeight: 600,
                                    textDecoration: "none",
                                    transition: "background 0.2s",
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "#b45309"}
                                onMouseLeave={e => e.currentTarget.style.background = C.amber}
                            >
                                Open in Google Maps ↗
                            </a>
                        </div>

                        {/* Embedded map — using the school's coordinates from the Google Maps link */}
                        <div style={{ width: "100%", height: 420, position: "relative" }}>
                            <iframe
                                title="New St. Francis School Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3663.8!2d75.6!3d22.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDM2JzAwLjAiTiA3NcKwMzYnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin&q=New+St+Francis+School+Pithampur+Sector+3+Dhar+MP"
                                width="100%"
                                height="420"
                                style={{ border: 0, display: "block" }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>

                        {/* Directions CTA strip */}
                        <div style={{
                            padding: "16px 24px",
                            background: "rgba(255,255,255,0.8)",
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12
                        }}>
                            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                                {[
                                    { icon: "🚗", label: "By Car", val: "Near Pithampur Industrial Area, Sector 3" },
                                    { icon: "📌", label: "Landmark", val: "Opposite Sector 3 Main Gate" },
                                ].map((d, i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 18 }}>{d.icon}</span>
                                        <div>
                                            <div style={{ fontFamily: "'Outfit'", fontSize: 11, color: C.amber, fontWeight: 700, textTransform: "uppercase" }}>{d.label}</div>
                                            <div style={{ fontSize: 13, color: C.textMid }}>{d.val}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <a
                                href="https://maps.app.goo.gl/ojcW58qZFrg7jrCF6?g_st=iw"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 6,
                                    background: C.blue, color: "#fff",
                                    padding: "8px 20px", borderRadius: 9999,
                                    fontFamily: "'Outfit'", fontSize: 13, fontWeight: 600,
                                    textDecoration: "none",
                                }}
                            >
                                Get Directions →
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

// ─── SISTER CAMPUS BRIDGE ─────────────────────────────────────────────────────
function SisterCampusPage({ setPage }) {
    return (
        <div style={{ paddingTop: 112, minHeight: "100vh", background: `linear-gradient(160deg, #eff6ff 0%, ${C.amberPale} 100%)` }}>
            <section style={{ padding: "100px 0", textAlign: "center" }}>
                <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px" }}>
                    <span className="tag" style={{ background: C.amberPale, color: C.amber, marginBottom: 20, display: "inline-block" }}>Sister Institution</span>
                    <h1 style={{ fontFamily: "'Outfit'", fontSize: "clamp(48px,6vw,72px)", fontWeight: 700, color: C.blue, marginBottom: 24, lineHeight: 1.15 }}>
                        You're visiting the<br /><span className="text-gradient">Branch Campus</span>
                    </h1>
                    <p style={{ fontSize: 18, fontWeight: 500, color: C.textMid, maxWidth: 560, margin: "0 auto 48px", lineHeight: 1.75 }}>
                        Both <strong style={{ color: C.blue }}>St. Francis Global Campus</strong> (Branch) and <strong style={{ color: C.blue }}>New St. Francis School</strong> (Main) are part of the same educational trust — offering the same legacy of excellence, now across two campuses.
                    </p>
                    <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
                        <button className="btn-primary" onClick={() => setPage("home")}>← Stay on Branch Campus</button>
                        <a href="https://newstfrancisschoolpithampur.in/" target="_blank" rel="noopener noreferrer" className="btn-amber" style={{ textDecoration: "none" }}>
                            Go to Main Campus →
                        </a>
                    </div>
                </div>
            </section>

            <section style={{ padding: "0 0 100px" }}>
                <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 24px" }}>
                    <h2 style={{ fontFamily: "'Outfit'", fontWeight: 700, fontSize: "clamp(36px,3vw,48px)", color: C.blue, textAlign: "center", marginBottom: 48 }}>Both campuses. <span style={{ color: C.amber }}>One legacy.</span></h2>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }} className="col-mobile-1">
                        <div className="glass-card glass-card-hover" style={{ borderRadius: 24, padding: "40px 36px", borderTop: `4px solid ${C.blue}` }}>
                            <div style={{ fontFamily: "'Outfit'", fontSize: 12, color: C.blueLight, fontWeight: 600, textTransform: "uppercase", marginBottom: 16 }}>MAIN CAMPUS</div>
                            <h3 style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.blue, marginBottom: 20 }}>New St. Francis School</h3>
                            {["Est. 2002", "1100+ Students", "Classes I–XII (CBSE)", "Located in City Centre", "96% Board Results", "Glassmorphic Modern Campus"].map((f, i) => (
                                <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                                    <span style={{ color: C.blue, fontSize: 12 }}>◆</span>
                                    <span style={{ fontSize: 16, color: C.textMid }}>{f}</span>
                                </div>
                            ))}
                            <a href="https://newstfrancisschoolpithampur.in/" target="_blank" rel="noopener noreferrer"
                                style={{ display: "inline-block", marginTop: 24, fontFamily: "'Outfit'", fontSize: 14, color: C.blue, fontWeight: 600, textDecoration: "none" }}>
                                Visit Main Campus →
                            </a>
                        </div>
                        <div className="glass-card glass-card-hover" style={{ borderRadius: 24, padding: "40px 36px", borderTop: `4px solid ${C.amber}` }}>
                            <div style={{ fontFamily: "'Outfit'", fontSize: 12, color: C.amber, fontWeight: 600, textTransform: "uppercase", marginBottom: 16 }}>BRANCH CAMPUS · YOU ARE HERE</div>
                            <h3 style={{ fontFamily: "'Outfit'", fontSize: 20, fontWeight: 700, color: C.blue, marginBottom: 20 }}>St. Francis Global Campus</h3>
                            {["Est. 2018", "680+ Students", "Classes I–XII (CBSE)", "Located in Green Valley", "98% Board Results", "Progressive Modern Campus"].map((f, i) => (
                                <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                                    <span style={{ color: C.amber, fontSize: 12 }}>◆</span>
                                    <span style={{ fontSize: 15, color: C.textMid }}>{f}</span>
                                </div>
                            ))}
                            <button onClick={() => setPage("home")} style={{ background: "none", border: "none", cursor: "pointer", marginTop: 24, fontFamily: "'Outfit'", fontSize: 14, color: C.amber, fontWeight: 600 }}>
                                Stay on Branch Campus →
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer({ setPage }) {
    const links = [
        { label: "Home", id: "home" },
        { label: "About", id: "about" },
        { label: "Academics", id: "academics" },
        { label: "Admissions", id: "admissions" },
        { label: "Gallery", id: "gallery" },
        { label: "Contact", id: "contact" },
    ];

    return (
        <footer style={{ background: `linear-gradient(to right, ${C.blueDark}, #1e3a8a, ${C.blueDark})`, color: "#fff" }}>
            <div style={{ maxWidth: 1200, margin: "0 auto", padding: "64px 24px 40px", display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 48 }} className="col-mobile-1">
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                        <img
                            src="https://customer-assets.emergentagent.com/job_89f0a926-232e-4f27-8bd9-23de150e4a0b/artifacts/ydf6w6ly_New%20St.%20Franic%20School%20Logo.jpg"
                            alt="New St. Francis School Logo"
                            style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(251,191,36,.6)", boxShadow: "0 0 0 3px rgba(251,191,36,.15)", flexShrink: 0 }}
                        />
                        <div>
                            <div style={{ fontFamily: "'Outfit'", fontWeight: 700, fontSize: 18, color: "#fff", lineHeight: 1.2 }}>New St. Francis School</div>
                            <div style={{ fontFamily: "'DM Sans'", fontSize: 11, color: C.amberLight, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.5, marginTop: 2 }}>Education is Above All</div>
                            <div style={{ fontFamily: "'DM Sans'", fontSize: 10, color: "rgba(255,255,255,.5)", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1.4, marginTop: 2 }}>Pithampur Sector-3, Dhar M.P.</div>
                        </div>
                    </div>
                    <p style={{ fontSize: 14, lineHeight: 1.625, color: "rgba(255,255,255,.6)", maxWidth: 300, marginBottom: 24 }}>
                        A progressive extension of the 24-year New St. Francis School legacy — committed to holistic, future-ready education.
                    </p>

                    {/* ── Social Media Icons ── */}
                    <div>
                        <div style={{ fontFamily: "'Outfit'", fontSize: 13, color: C.amberLight, fontWeight: 600, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Follow Us
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            {/* Instagram */}
                            <a
                                href="https://www.instagram.com/newstfrancishighschool?igsh=MTZ3cm8yd3YwdXdpeQ%3D%3D&utm_source=qr"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-icon-btn"
                                title="Follow us on Instagram"
                                style={{ color: "#fff" }}
                            >
                                <InstagramIcon size={18} />
                            </a>
                            {/* Facebook */}
                            <a
                                href="https://www.facebook.com/share/18Lo4SnE3V/?mibextid=wwXIfr"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-icon-btn"
                                title="Follow us on Facebook"
                                style={{ color: "#fff" }}
                            >
                                <FacebookIcon size={18} />
                            </a>
                        </div>
                    </div>
                </div>

                <div>
                    <div style={{ fontFamily: "'Outfit'", fontSize: 18, fontWeight: 600, color: C.amberLight, marginBottom: 20 }}>Quick Links</div>
                    {links.map(l => (
                        <button key={l.id} onClick={() => setPage(l.id)} className="ul-hover"
                            style={{ display: "block", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,.65)", fontSize: 14, fontWeight: 400, marginBottom: 10, textAlign: "left", fontFamily: "'DM Sans'" }}>
                            {l.label}
                        </button>
                    ))}
                </div>

                <div>
                    <div style={{ fontFamily: "'Outfit'", fontSize: 18, fontWeight: 600, color: C.amberLight, marginBottom: 20 }}>Contact</div>
                    {["Plot 24, Sector 7, Green Valley,", "Nagpur — 440010", "", "+91 97554 02604 | +91 98277 82604", "info@sfgc.edu.in"].map((l, i) => (
                        <div key={i} style={{ fontSize: 14, fontWeight: 400, color: "rgba(255,255,255,.6)", marginBottom: 6 }}>{l}</div>
                    ))}
                    <a href="https://newstfrancisschoolpithampur.in/">
                        <button style={{ marginTop: 20, background: "none", border: "none", cursor: "pointer", fontFamily: "'Outfit'", fontSize: 12, color: C.amberLight, fontWeight: 700, textTransform: "uppercase" }}>
                            ⇄ MAIN CAMPUS
                        </button>
                    </a>
                </div>
            </div>

            {/* ── Bottom bar — updated copyright ── */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,.1)", padding: "20px 24px", maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <span style={{ fontFamily: "'DM Sans'", fontSize: 14, color: "rgba(255,255,255,.45)" }}>
                    © 2026 New St. Francis School, Pithampur. All rights reserved. | Designed By WSchools
                </span>
                <span style={{ fontFamily: "'DM Sans'", fontSize: 12, fontWeight: 400, color: "rgba(255,255,255,.45)" }}>
                    Sister Institution: New St. Francis School (Main Campus)
                </span>
            </div>
        </footer>
    );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function BranchSchoolApp() {
    injectStyles("branch-school-styles", GLOBAL_CSS);
    const [page, setPage] = useState("home");

    const navigate = (p) => {
        setPage(p);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const renderPage = () => {
        switch (page) {
            case "home": return <HomePage setPage={navigate} />;
            case "about": return <AboutPage />;
            case "academics": return <AcademicsPage />;
            case "admissions": return <AdmissionsPage />;
            case "gallery": return <GalleryPage />;
            case "contact": return <ContactPage />;
            case "sister": return <SisterCampusPage setPage={navigate} />;
            default: return <HomePage setPage={navigate} />;
        }
    };

    return (
        <div className="br-root">
            <Navbar page={page} setPage={navigate} />
            <main>{renderPage()}</main>
            <Footer setPage={navigate} />
        </div>
    );
}